import { reactive, ref, watchEffect, watch, type Ref } from 'vue'
import { useAuth } from './useAuth'
import { usePensionCalculations } from './usePensionCalculations'
import { api } from '@/lib/api'
import { router } from '@/router'
import { isNavigationFailure, NavigationFailureType } from 'vue-router'

const routes = ref<string[]>([])
const currentRouteIndex = ref(0)
const isFromQuestionnaire = ref(false)
const formData = reactive<Record<string, any>>({})
const selectedForms = ref<string[]>([])
const selectedIncomeTypes = ref<string[]>([])

// Key for localStorage persistence
const STORAGE_KEY = 'form11Progress'

interface Form11Composable {
  isFromQuestionnaire: Ref<boolean>
  formData: Record<string, any>
  selectedForms: Ref<string[]>
  selectedIncomeTypes: Ref<string[]>
  setup: (selectedRoutes: string[]) => Promise<void>
  saveSelectedIncomes: (incomeTypes: string[]) => void
  goNext: (
    question: string,
    answer: any,
    callback?: () => void,
  ) => Promise<void>
  goBack: () => void
  completeForm: () => Promise<void>
  pension: Ref<number>
  maxPension: Ref<number>
  taxback: Ref<number>
  maxTaxback: Ref<number>
  resume: () => Promise<void>
  hasSavedProgress: () => boolean
  registerDraft: (question: string, reactiveState: any) => () => void
}

const { pension, maxPension, taxback, maxTaxback, totalTaxbackCredits } =
  usePensionCalculations()
const { refreshProfile } = useAuth()

watchEffect(() => {
  if (isFromQuestionnaire.value) {
    console.log('set: is from questionnaire')
  } else {
    console.log('set: is not from questionnaire')
  }
})

async function setup(selectedRoutes: string[]) {
  routes.value = selectedRoutes
  selectedForms.value = selectedRoutes
  currentRouteIndex.value = 0
  isFromQuestionnaire.value = true

  console.log('useForm11.setup', selectedRoutes)

  if (selectedRoutes.length === 0) {
    router.push('/tax-refund-options')
    return
  }

  console.log('useForm11.setup: navigating to', selectedRoutes[0])
  const navResult = await router.replace(selectedRoutes[0])

  if (isNavigationFailure(navResult, NavigationFailureType.aborted)) {
    console.warn('Navigation aborted, retrying with push')
    await router.push(selectedRoutes[0])
  }
}

function saveSelectedIncomes(incomeTypes: string[]) {
  selectedIncomeTypes.value = incomeTypes
}

async function submitAnswer(question: string, answer: any): Promise<void> {
  try {
    if (!isFromQuestionnaire.value) {
      await api.post('/answer', { question, answer })
    } else {
      formData[question] = answer
    }

    setTimeout(() => {
      useAuth().refreshProfile()
    }, 700)
  } catch (error) {
    console.error('Failed to submit form:', error)
    throw error
  }
}

async function goNext(question: string, answer: any, callback?: () => void) {
  try {
    await submitAnswer(question, answer)
  } catch (error) {
    console.error('Failed to submit form:', error)
  }

  completeForm(callback)
}

async function completeForm(callback?: () => void) {
  if (!isFromQuestionnaire.value) {
    console.log('is not from questionnaire')
    if (callback) {
      callback()
    } else {
      router.push('/tax-refund-submitted')
    }
    return
  }

  const nextRouteIndex = currentRouteIndex.value + 1
  currentRouteIndex.value = nextRouteIndex

  const nextRoute = routes.value[nextRouteIndex]

  if (nextRoute) {
    router.push(nextRoute)
    return
  }

  await api.post('/answer-multiple', { questions: formData })

  // Refresh profile to get the latest taxback amount
  await refreshProfile()

  router.push('/tax-refund-submitted')

  // Clear persisted progress once the form is successfully completed
  clearPersistedProgress()

  resetForm11()
}

function resetForm11() {
  for (const key in formData) {
    delete formData[key]
  }
  selectedForms.value = []
  selectedIncomeTypes.value = []
  currentRouteIndex.value = 0
  isFromQuestionnaire.value = false
  clearPersistedProgress()
}

function goBack() {
  router.back()
}

// Helper to serialise the current composable state
function serialize() {
  return {
    routes: routes.value,
    currentRouteIndex: currentRouteIndex.value,
    isFromQuestionnaire: isFromQuestionnaire.value,
    formData: { ...formData },
    selectedForms: selectedForms.value,
    selectedIncomeTypes: selectedIncomeTypes.value,
  }
}

// Persist progress whenever relevant reactive state changes
watchEffect(() => {
  // Only persist if the flow is coming from the questionnaire and we actually have selected routes
  if (isFromQuestionnaire.value && routes.value.length > 0) {
    console.log('persisting progress')
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialize()))
  }
})

function clearPersistedProgress() {
  localStorage.removeItem(STORAGE_KEY)
}

function hasSavedProgress(): boolean {
  return !!localStorage.getItem(STORAGE_KEY)
}

/**
 * Components can call this to automatically synchronise their local reactive state
 * into the formData store while the user types, without waiting for an explicit submit.
 * Returns a cleanup function to stop watching (call it in onUnmounted).
 */
function registerDraft(question: string, reactiveState: any): () => void {
  // initial sync
  formData[question] = JSON.parse(JSON.stringify(reactiveState))

  // deep-watch the reactive state
  const stop = watch(
    reactiveState,
    (val) => {
      formData[question] = JSON.parse(JSON.stringify(val))
    },
    { deep: true },
  )

  return stop
}

async function resume() {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return

  try {
    const data = JSON.parse(raw)

    routes.value = data.routes || []
    selectedForms.value = data.selectedForms || []
    selectedIncomeTypes.value = data.selectedIncomeTypes || []
    currentRouteIndex.value = data.currentRouteIndex || 0
    isFromQuestionnaire.value = data.isFromQuestionnaire ?? true

    // Reset and hydrate formData
    for (const key in formData) {
      delete formData[key]
    }
    Object.assign(formData, data.formData || {})

    // Rebuild navigation history so the user can navigate back to previous questions
    if (routes.value.length > 0) {
      // Replace current route (TaxRefundQuestions) with the first form route
      await router.replace(routes.value[0])

      // Push intermediate routes up to the saved index
      for (
        let i = 1;
        i <= currentRouteIndex.value && i < routes.value.length;
        i++
      ) {
        await router.push(routes.value[i])
      }
    }
  } catch (e) {
    console.error('Failed to resume saved Form 11 progress', e)
    clearPersistedProgress()
  }
}

export function useForm11(): Form11Composable {
  return {
    isFromQuestionnaire,
    setup,
    saveSelectedIncomes,
    goNext,
    goBack,
    completeForm,
    formData,
    selectedForms,
    selectedIncomeTypes,
    pension,
    maxPension,
    taxback,
    maxTaxback,
    resume,
    hasSavedProgress,
    registerDraft,
  }
}
